import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plan } from './types/planTypes';
import { updatePlan } from '@/utils/planUtils';

interface EditPlanModalProps {
  plan: Plan | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedPlan: Plan) => void;
}

const EditPlanModal: React.FC<EditPlanModalProps> = ({
  plan,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { toast } = useToast();
  const [planName, setPlanName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [isAddOn, setIsAddOn] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when plan data is available
  useEffect(() => {
    if (plan) {
      setPlanName(plan.plan_name || '');
      setDisplayName(plan.display_name || '');
      setIsAddOn(plan.add_on || false);
    }
  }, [plan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!plan?.id) {
      toast({
        title: "Error",
        description: "Plan ID is missing",
        variant: "destructive"
      });
      return;
    }

    // Validate inputs
    if (!planName.trim()) {
      toast({
        title: "Error",
        description: "Plan name is required",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updatePlan(
        plan.id,
        {
          plan_name: planName,
          display_name: displayName || planName, // Use planName as fallback if displayName is empty
          add_on: isAddOn
        }
      );

      if (result.success && result.updatedPlan) {
        toast({
          title: "Success",
          description: "Plan updated successfully"
        });
        onSuccess(result.updatedPlan);
        onClose();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update plan",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Edit Plan
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label htmlFor="planName">Plan Name</Label>
            <Input
              id="planName"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Enter plan name"
              required
            />
          </div>

          <div>
            <Label htmlFor="displayName">Display Name (Optional)</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter display name"
            />
            <p className="text-xs text-gray-500 mt-1">
              If left empty, Plan Name will be used
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="isAddOn" 
              checked={isAddOn}
              onCheckedChange={(checked) => setIsAddOn(checked === true)}
            />
            <Label 
              htmlFor="isAddOn" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              This is an add-on plan
            </Label>
          </div>

          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPlanModal;
